import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ArrowLeft, BookOpen, MessageCircle, Send, Loader2 } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";

const BookDiscussionDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, api } = useAuth();
  const [discussion, setDiscussion] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchDiscussion = async () => {
      try {
        const res = await api.get(`/book-discussions/${id}`);
        setDiscussion(res.data);
        setComments(res.data.comments || []);
      } catch (error) {
        
        navigate('/reading');
      } finally {
        setLoading(false);
      }
    };

    fetchDiscussion();
  }, [id, api, navigate]);

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    setSubmitting(true);
    try {
      const res = await api.post(`/book-discussions/${id}/comments`, {
        content: newComment.trim()
      });
      setComments(prev => [...prev, res.data]);
      setNewComment('');
    } catch (error) {
      // Non-critical error suppressed
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-12">
        <Skeleton className="h-8 w-32 mb-8" />
        <Skeleton className="h-48 w-full mb-6" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  if (!discussion) return null;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="book-discussion-detail">
      {/* Back Link */}
      <Link
        to="/reading"
        className="inline-flex items-center gap-2 text-text-muted hover:text-text-primary mb-8 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Discussions
      </Link>

      {/* Discussion Header */}
      <Card className="bg-white border-brand-primary/10 mb-8 overflow-hidden">
        {discussion.cover_image && (
          <div className="h-56 overflow-hidden">
            <img
              src={discussion.cover_image.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${discussion.cover_image}` : discussion.cover_image}
              alt={discussion.book_title}
              className="w-full h-full object-cover"
              style={{ objectPosition: `center ${discussion.cover_image_position || 50}%` }}
            />
          </div>
        )}
        <CardContent className="p-8">
          <div className="flex items-center gap-2 mb-3">
            <BookOpen className="w-5 h-5 text-brand-accent" />
            <span className="text-sm text-text-muted">Book Discussion</span>
          </div>
          <h1 className="font-serif text-3xl font-semibold text-text-primary mb-1">
            {discussion.book_title}
          </h1>
          {discussion.book_author && (
            <p className="text-text-secondary mb-4">by {discussion.book_author}</p>
          )}
          <p className="text-text-primary leading-relaxed whitespace-pre-wrap">
            {discussion.discussion_prompt}
          </p>
          <div className="mt-4 pt-4 border-t border-brand-primary/10 flex items-center justify-between text-sm text-text-muted">
            <span>Started by {discussion.created_by_name}</span>
            <span>{new Date(discussion.created_at).toLocaleDateString()}</span>
          </div>
        </CardContent>
      </Card>

      {/* Comments Section */}
      <section data-testid="discussion-comments-section">
        <h2 className="font-serif text-xl font-semibold text-text-primary mb-6 flex items-center gap-2">
          <MessageCircle className="w-5 h-5 text-brand-accent" />
          Discussion ({comments.length})
        </h2>

        {/* Comment Form */}
        {user ? (
          <form onSubmit={handleSubmitComment} className="mb-8" data-testid="comment-form">
            <div className="flex gap-3">
              <Avatar className="w-9 h-9 flex-shrink-0">
                <AvatarImage src={user.avatar_url} />
                <AvatarFallback className="bg-brand-primary text-white text-sm">
                  {user.display_name?.[0]?.toUpperCase() || user.username?.[0]?.toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <Textarea
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Share your thoughts on this book..."
                  rows={3}
                  className="resize-none border-brand-primary/15 mb-2"
                  data-testid="discussion-comment-input"
                />
                <div className="flex justify-end">
                  <Button
                    type="submit"
                    className="btn-accent gap-2"
                    disabled={submitting || !newComment.trim()}
                    data-testid="submit-discussion-comment"
                  >
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    Post Comment
                  </Button>
                </div>
              </div>
            </div>
          </form>
        ) : (
          <div className="bg-surface-alt rounded-lg p-6 mb-8 text-center">
            <p className="text-text-secondary">
              <Link to="/login" className="text-brand-accent hover:underline">Log in</Link> to join the discussion
            </p>
          </div>
        )}

        {/* Comments List */}
        {comments.length === 0 ? (
          <div className="text-center py-12">
            <MessageCircle className="w-12 h-12 text-text-muted mx-auto mb-4" />
            <p className="text-text-secondary">No comments yet. Be the first to share your thoughts!</p>
          </div>
        ) : (
          <div className="space-y-4">
            {comments.map((comment) => (
              <div
                key={comment.id}
                className="flex gap-3"
                data-testid={`discussion-comment-${comment.id}`}
              >
                <Link to={`/writer/${comment.author_id}`}>
                  <Avatar className="w-9 h-9 flex-shrink-0">
                    <AvatarImage src={comment.author_avatar} />
                    <AvatarFallback className="bg-brand-primary text-white text-sm">
                      {comment.author_name?.[0]?.toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                </Link>
                <div className="flex-1 bg-white rounded-xl border border-brand-primary/10 p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Link
                      to={`/writer/${comment.author_id}`}
                      className="text-sm font-medium text-text-primary hover:text-brand-accent"
                    >
                      {comment.author_name}
                    </Link>
                    <span className="text-xs text-text-muted">
                      {new Date(comment.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <p className="text-text-secondary text-sm leading-relaxed whitespace-pre-wrap">
                    {comment.content}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default BookDiscussionDetailPage;
